<template>
  <section class="listing">
    <div class="header">
      <div class="title">Vendas</div>
      <button @click="print" class="button-save"><i class="fas fa-print"></i><span>Imprimir</span></button>
    </div>
    <form @submit.prevent class="form" style="padding: 0">
      <div class="form-search" style="padding: 0">
        <date-picker v-model="period" @change="getData" range placeholder="Selecione um período" format="DD/MM/YYYY"></date-picker>
        <div class="search-input">
          <input v-model="search" type="text" placeholder="Pesquisar..." />
          <i class="fas fa-search show-search"></i>
        </div>
      </div>
      <div class="form-search">
        <select v-model="type" @change="getData" style="width: 110px">
          <option value="tickets">Bilheteria</option>
          <option value="tiketera">Online</option>
          <option value="canceled">Cancelados</option>
        </select>
        <select v-model="show" style="flex-grow: 1">
          <option :value="null">Todos os eventos</option>
          <option v-for="s in shows" :value="s" :key="s">{{ s }}</option>
        </select>
        <select v-model="paymentMethod" style="width: 150px">
          <option :value="null">Todas as formas</option>
          <option value="cash">Dinheiro</option>
          <option value="card">Cartão de Crédito</option>
          <option value="debit">Cartão de Débito</option>
          <option value="external">Voucher</option>
          <option value="courtesy">Cortesia</option>
          <option value="billet">Boleto</option>
        </select>
      </div>
    </form>
    <div class="print-header">
      <div class="print-title">Vendas</div>
    </div>
    <transition name="show" mode="out-in">
      <i v-if="loading" class="fas fa-spinner loading-big"></i>
      <div v-if="!loading" style="margin-top: 10px">
        <div class="sum-group">
          <div class="sum">
            <span>Ticket Médio</span>
            <span>{{ average.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
          <div class="sum">
            <span>Valor Total</span>
            <span>{{ total.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
        </div>
        <div v-if="!paymentMethod" class="sum-group">
          <div class="sum sum2">
            <span>Dinheiro</span>
            <span>{{ cash.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
          <div class="sum sum2">
            <span>Crédito</span>
            <span>{{ card.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
        </div>
        <div v-if="!paymentMethod" class="sum-group">
          <div class="sum sum2">
            <span>Débito</span>
            <span>{{ debit.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
          <div class="sum sum2">
            <span>Voucher</span>
            <span>{{ external.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
        </div>
        <div v-if="!paymentMethod" class="sum-group">
          <div class="sum sum2">
            <span>Boleto</span>
            <span>{{ billet.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</span>
          </div>
        </div>
        <div v-for="(group, i) in grouped" :key="`group-${i}`" class="group" :class="{ 'open' : open.includes(group.date) }">
          <div @click="open.includes(group.date) ? open = open.filter(x => x !== group.date) : open.push(group.date)" class="group-header">
            <i class="fas" :class="open.includes(group.date) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            <div class="group-date">{{ getDay(group.date) }} {{ getMonthText(group.date) }} {{ getYear(group.date) }}</div>
            <div v-if="!open.includes(group.date)" class="group-total">{{ group.total.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}}</div>
            <div v-else class="group-total">
              <b>{{ type === 'tiketera' ? 'Vendas' : 'Ingressos' }}:</b> {{ group.items.length }} <br/>
              <b>Ticket Médio:</b> {{ (group.total / group.items.length).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }} <br/>
              <b>Total:</b> {{ group.total.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}
            </div>
          </div>
          <div v-for="(x, index) in group.items" :key="`item-${index}`" class="list">
            <div class="time-text">
              <span>{{ getHours(x.date) }}</span>
              <span>{{ getMinutes(x.date) }}</span>
            </div>
            <div class="data">
              <div class="main-data">{{ x.customer || x.ticketName || x.name || '-' }}</div>
              <div class="detail-data">{{ x.seat && x.seat.seat ? x.seat.seat : x.seat }} - {{ x.show }} ({{ getDay(x.showDate) + '/' + getMonth(x.showDate) }})</div>
            </div>
            <div v-if="type === 'tiketera'" style="text-align: right">
              <div class="main-data">{{ x.amount.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) }}</div>
              <div class="detail-data">{{ x.tickets }} ingresso(s)</div>
            </div>
            <div v-else style="text-align: right">
              <div class="main-data">{{ x.price ? x.price.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' }) : '-' }}</div>
              <div class="detail-data">{{ x.ticketType }} - {{ x.paymentMethodText }}</div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
import logger from '../../logger'
import { getForReport as getTickets } from '../api/ticket'
import { getAll as getTiketera } from '../api/transaction'
import { getAll as getCanceled } from '../api/ticketCanceled'

export default {
  name: 'Sales',
  data () {
    return {
      loading: true,
      type: 'tickets',
      data: [],
      period: [],
      search: '',
      show: null,
      paymentMethod: null,
      open: []
    }
  },
  computed: {
    shows () {
      return this.data
        .map(x => x.show)
        .filter((x, i, a) => a.indexOf(x) === i)
        .sort()
    },
    filtered () {
      return this.data
        .filter(x => this.show ? x.show === this.show : true)
        .filter(x => this.paymentMethod ? x.paymentMethod === this.paymentMethod : true)
        .filter(x => Object.keys(x).some(key => String(x[key]).toUpperCase().includes(this.search.toUpperCase())))
    },
    grouped () {
      return this.filtered.reduce((acc, cur) => {
        const found = acc.find(x => x.date === cur.date.split(' ')[0])
        if (found) {
          found.items.push(cur)
          found.total += cur.price || cur.amount || 0
        } else acc.push({ date: cur.date.split(' ')[0], items: [cur], total: cur.price || cur.amount || 0 })
        return acc
      }, [])
    },
    total () {
      return this.filtered
        .map(y => y.price || y.amount || 0)
        .reduce((acc, cur) => acc + Number(cur), 0)
    },
    average () {
      return this.total === 0 ? 0 : this.total / this.filtered.length
    },
    cash () {
      return this.filtered
        .filter(x => x.paymentMethod === 'cash')
        .map(y => y.price || y.amount || 0)
        .reduce((acc, cur) => acc + Number(cur), 0)
    },
    card () {
      return this.filtered
        .filter(x => x.paymentMethod === 'card')
        .map(y => y.price || y.amount || 0)
        .reduce((acc, cur) => acc + Number(cur), 0)
    },
    billet () {
      return this.filtered
        .filter(x => x.paymentMethod === 'billet')
        .map(y => y.price || y.amount || 0)
        .reduce((acc, cur) => acc + Number(cur), 0)
    },
    debit () {
      return this.filtered
        .filter(x => x.paymentMethod === 'debit')
        .map(y => y.price || y.amount || 0)
        .reduce((acc, cur) => acc + Number(cur), 0)
    },
    external () {
      return this.filtered
        .filter(x => x.paymentMethod === 'external')
        .map(y => y.price || y.amount || 0)
        .reduce((acc, cur) => acc + Number(cur), 0)
    }
  },
  async mounted () {
    try {
      // const date = new Date()
      // date.setMonth(0)
      this.period = [new Date(), new Date()]
      this.getData()
    } catch (error) {
      logger.error(error, this)
    }
  },
  methods: {
    async getData () {
      if (!this.period[0] || !this.period[1]) return false
      this.loading = true
      this.open = []
      try {
        let data = []
        if (this.type === 'tickets') data = await getTickets(this.period[0].toISOString().split('T')[0], this.period[1].toISOString().split('T')[0])
        else if (this.type === 'tiketera') data = await getTiketera(this.period[0].toISOString().split('T')[0], this.period[1].toISOString().split('T')[0])
        else if (this.type === 'canceled') data = await getCanceled(this.period[0].toISOString().split('T')[0], this.period[1].toISOString().split('T')[0])
        this.data = data.map(x => {
          if (x.paymentMethod === 'cash') x.paymentMethodText = 'Dinheiro'
          else if (x.paymentMethod === 'card') x.paymentMethodText = 'Cartão de Crédito'
          else if (x.paymentMethod === 'debit') x.paymentMethodText = 'Cartão de Débito'
          else if (x.paymentMethod === 'external') x.paymentMethodText = 'Voucher'
          else if (x.paymentMethod === 'courtesy') x.paymentMethodText = 'Cortesia'
          else x.paymentMethodText = '-'
          return x
        })
      } catch (error) {
        logger.warn(error)
      }
      this.loading = false
    },
    print () {
      this.open = this.grouped.map(x => x.date)
      this.$nextTick(() => window.print())
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.group {
  margin: 5px 5px;
  border: 1px solid $secondary-color;
  border-radius: 5px;
  .group-header {
    display: flex;
    flex-wrap: wrap;
    color: $primary-color;
    padding: 10px 15px;
    cursor: pointer;
    .group-date {
      flex-grow: 1;
      font-weight: bold;
      padding: 0 10px;
    }
    .group-total {
      text-align: right;
      // font-weight: bold;
    }
  }
  .list {
    display: none;
  }
  &.open {
    padding-bottom: 5px;
    .group-header {
      border-bottom: 1px dashed #ccc;
    }
    .list {
      display: flex;
    }
  }
}

.calendar {
  width: 40px;
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-image: linear-gradient(to bottom right, $primary-color, $secondary-color);
  border-radius: 5px;
  margin-right: 10px;
  font-weight: 500;
  span:nth-child(1) {
    font-size: 1.2em;
  }
  span:nth-child(2) {
    font-size: 0.7em;
  }
}

.time-text {
  font-size: 2.2em;
  font-weight: 400;
  margin-right: 10px;
  color: #555;
  display: flex;
  span:nth-child(2) {
    font-size: 0.5em;
    line-height: 2em;
  }
}

.form-search {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 0;
  padding-bottom: 10px;

  input, select {
    width: 100%;
    height: 34px;
    margin: 5px !important;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
    position: relative;
    input {
      width: calc(100% - 10px) !important;
    }
    i {
      position: absolute;
      top: 11px;
      right: 10px;
      color: #666;
      font-size: 1.5em;
    }
  }
}

.button-action:hover {
  i {
    animation-name: ticket-image !important;
    animation-duration: 0.5s !important;
    animation-iteration-count: 1 !important;
  }
}

.sum-group {
  display: flex;
  flex-wrap: wrap;
  .sum {
    display: flex;
    flex-basis: calc(100% - 10px);
    margin: 0 5px 10px;
    span {
      text-align: center;
      padding: 8px 0;
      flex-basis: 50%;
    }
    span:nth-child(1) {
      color: #fff;
      background-image: linear-gradient(to bottom right, $primary-color, $secondary-color);
      border-radius: 5px 0 0 5px;
      border: 1px solid $secondary-color;
    }
    span:nth-child(2) {
      font-weight: bold;
      color: $secondary-color;
      border-radius: 0 5px 5px 0;
      border: 1px solid $secondary-color;
    }
  }
  .sum2 {
    span:nth-child(1) {
      color: #555;
      background: #e5e5e5;
      border: 1px solid #555;
      border-right: none;
      font-weight: bold;
    }
    span:nth-child(2) {
      color: #555;
      border: 1px solid #555;
    }
  }
}

.print-header {
  display: none;
  justify-content: space-between;
  align-items: center;
  border-bottom: 5px solid $primary-color;
  padding: 20px 15px;
  margin-bottom: 20px !important;
}

.print-title {
  font-size: 2em;
  color: $primary-color;
  font-weight: 500;
}

// Desktop
@media screen and (min-width: $desktop-width) {
  .sum-group {
    .sum {
      flex-basis: calc(50% - 10px);
    }
  }
  .form-search {
    input, select {
      width: auto;
    }
  }
}

@media print {
  .header, .form-search {
    display: none !important;
  }
  .print-header {
    display: block !important;
  }
  .sum-group {
    .sum {
      flex-basis: calc(50% - 10px);
    }
  }
  .list {
    page-break-inside: avoid;
    page-break-after: auto;
  }
}
</style>

<style lang="scss">
.mx-datepicker {
  max-width: 230px;
  input {
    margin: 5px !important;
    width: calc(100% - 10px);
  }
  i {
    right: 15px;
  }
}
</style>
